'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import GlideDefault from '@glidejs/glide/dist/glide.esm.js';
import classnames from 'classnames';
import * as React from 'react';
import { Button } from '../Button/Button';
const Glide = (_a = GlideDefault.default) !== null && _a !== void 0 ? _a : GlideDefault;
const defaultBreakpoints = {
    1200: {
        perView: 6,
        gap: 64,
    },
    1080: {
        perView: 5,
        gap: 64,
    },
    960: {
        perView: 5,
        gap: 64,
    },
    768: {
        perView: 4,
        gap: 16,
    },
    560: {
        perView: 3,
        gap: 16,
    },
    375: {
        perView: 3,
        gap: 16,
    },
};
export const HorizontalOverflowCarousel = React.forwardRef((_a, ref) => {
    var { id, className, children, rewindDefault = false, startAtDefault = 0, perViewDefault = 6, defaultGap = 32, arrowsOut, animationDuration = 400, autoplay = false, movementType = 'slider', breakpoints = defaultBreakpoints, modifier } = _a, other = __rest(_a, ["id", "className", "children", "rewindDefault", "startAtDefault", "perViewDefault", "defaultGap", "arrowsOut", "animationDuration", "autoplay", "movementType", "breakpoints", "modifier"]);
    const carouselRef = React.useRef(null);
    const buttonNext = React.useRef(null);
    const buttonPrev = React.useRef(null);
    const modifierGlide = () => {
        return {
            modify(translate) {
                return modifier ? modifier(translate) : translate;
            },
        };
    };
    const componentClassName = classnames('sparky-c-horizontal-overflow-carousel-wrapper', className, {
        'sparky-c-horizontal-overflow-carousel-arrows-out': arrowsOut === true,
    });
    const ArrowDisabler = function (Glide, Components) {
        return {
            mount() {
                if (!Glide.on) {
                    console.warn('Glide.on not found');
                    return;
                }
                Glide.on(['mount.after', 'run', 'resize'], () => {
                    if (buttonPrev.current) {
                        if (Glide.index === 0 && movementType !== 'carousel') {
                            buttonPrev.current.classList.add('glide__arrow--disabled');
                            buttonPrev.current.setAttribute('disabled', 'disabled');
                        }
                        else {
                            buttonPrev.current.classList.remove('glide__arrow--disabled');
                            buttonPrev.current.removeAttribute('disabled');
                        }
                    }
                    if (buttonNext) {
                        if (!Glide.settings)
                            return;
                        const componentsSize = Components.Sizes && typeof Components.Sizes.length === 'number'
                            ? Components.Sizes.length
                            : 0;
                        if (Glide.index === componentsSize - Glide.settings.perView &&
                            movementType !== 'carousel') {
                            if (buttonNext.current) {
                                buttonNext.current.classList.add('glide__arrow--disabled');
                                buttonNext.current.setAttribute('disabled', 'disabled');
                            }
                        }
                        else {
                            if (buttonNext.current) {
                                buttonNext.current.classList.remove('glide__arrow--disabled');
                                buttonNext.current.removeAttribute('disabled');
                            }
                        }
                    }
                });
            },
        };
    };
    React.useImperativeHandle(ref, () => {
        if (!carouselRef.current)
            return document.createElement('div');
        return carouselRef.current;
    });
    React.useEffect(() => {
        var _a;
        const disableDrag = ((_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('.sparky-c-horizontal-overflow-carousel__item').length) == perViewDefault
            ? 0
            : 45;
        const options = {
            startAt: startAtDefault,
            perView: perViewDefault,
            rewind: rewindDefault,
            gap: defaultGap,
            breakpoints,
            bound: true,
            keyboard: false,
            touchAngle: disableDrag,
            animationDuration,
            autoplay,
            type: movementType,
        };
        if (carouselRef.current === null)
            return;
        const glide = new Glide(carouselRef.current, options);
        const glideInstance = glide.mutate([modifierGlide]).mount({
            ArrowDisabler: ArrowDisabler,
        });
        const handleResize = () => glideInstance.go('<<');
        const eventType = window.innerWidth < 768 ? 'orientationchange' : 'resize';
        window.addEventListener(eventType, handleResize);
        return () => {
            glideInstance.destroy();
            window.removeEventListener(eventType, handleResize);
        };
    }, [
        componentClassName,
        breakpoints,
        perViewDefault,
        rewindDefault,
        startAtDefault,
        defaultGap,
        autoplay,
        animationDuration,
        movementType,
        modifier,
    ]);
    return (_jsxs("div", Object.assign({ id: id, className: componentClassName, ref: carouselRef }, { children: [_jsx("div", Object.assign({ className: "sparky-c-horizontal-overflow-carousel__inner" }, other, { "data-testid": "overflowscroll", "data-glide-el": "track" }, { children: _jsx("ol", Object.assign({ className: "sparky-c-horizontal-overflow-carousel__list" }, { children: children })) })), _jsxs("div", Object.assign({ className: "sparky-c-horizontal-overflow-carousel__controls", "data-glide-el": "controls" }, { children: [_jsx(Button, { circle: true, text: "arrow-prev", iconName: "chevron-left", iconPosition: "before", iconSize: "md", hideText: true, variant: "primary", className: "sparky-c-horizontal-overflow-carousel__button sparky-c-horizontal-overflow-carousel__button-prev", "data-glide-dir": "|<", buttonRef: buttonPrev }), _jsx(Button, { circle: true, text: "arrow-next", iconName: "chevron-right", iconPosition: "before", iconSize: "md", hideText: true, variant: "primary", className: "sparky-c-horizontal-overflow-carousel__button sparky-c-horizontal-overflow-carousel__button-next", "data-glide-dir": "|>", buttonRef: buttonNext })] }))] })));
});
HorizontalOverflowCarousel.displayName = 'HorizontalOverflowCarousel';
//# sourceMappingURL=HorizontalOverflowCarousel.js.map